<script>
import Nav from '@/components/Nav.vue'
import Footer from '@/components/Footer.vue'

export default {
    name: 'Scope',
    components: { NavComponent: Nav, FooterComponent: Footer },
    data() {
        return {
            fields: [
                { key: 'responsibility', label: 'Responsibility' },
                { key: 'covered', label: 'Covered?', tdClass: 'status-col', thClass: 'status-col' },
                { key: 'billable', label: 'Billable?', tdClass: 'status-col', thClass: 'status-col' },
            ],
            sections: [
                {
                    title: 'ShiftOS',
                    items: [
                        { responsibility: 'ShiftOS onboarding (sales handoff, AnyDesk remote setup, Steam installation, Assetto Corsa installation, Content Manager setup, and ShiftOS Launcher configuration)', covered: true, billable: false },
                        { responsibility: 'ShiftOS customer support, troubleshooting, bug reports, and feature requests', covered: true, billable: false },
                        { responsibility: 'Customer support requests (email, website, social media, phone, and ticket systems, triaging tickets)', covered: true, billable: false },
                        { responsibility: 'Ticket management and customer follow-up', covered: true, billable: false },
                    ],
                },
                {
                    title: 'Shift Arcade Franchisee',
                    items: [
                        { responsibility: 'Franchise onboarding, troubleshooting, technical support, and ShiftOS support coordination', covered: true, billable: false },
                        { responsibility: 'D-Box or Sigma software and physical hardware configuration', covered: true, billable: false },
                        { responsibility: 'Physical travel to SimStation location with reimbursement', covered: 'optional', billable: true },
                    ],
                },
                {
                    title: 'SimStation',
                    items: [
                        { responsibility: 'SimStation onboarding (likely via AnyDesk), support, troubleshooting, and sim hardware support', covered: true, billable: false },
                        { responsibility: 'D-Box or Sigma software and physical hardware configuration', covered: true, billable: false },
                        { responsibility: 'Physical travel to SimStation location with reimbursement', covered: 'optional', billable: true },
                    ],
                },
                {
                    title: 'Billable Sim Racing Services',
                    items: [
                        { responsibility: 'General public sim racing technical support', covered: true, billable: true },
                        { responsibility: 'Sim racing software installation, configuration, troubleshooting, and optimization (SimHub, CrewChief, iRacing, Nvidia Control Panel, third-party sim racing applications, etc.)', covered: true, billable: true },
                        { responsibility: 'Non-ShiftOS hardware support, installation, and sim hardware configuration', covered: true, billable: true },
                    ],
                },
                {
                    title: 'Not Handled by Support',
                    items: [
                        { responsibility: 'Walk-in customers, active in-store customer assistance, retail counter operations, appointments, and arcade floor management', covered: false, billable: false },
                    ],
                },
            ],
            salesOpportunities: [
                'Triple monitor and Nvidia Surround setup',
                'SimHub dashboards and haptics setup',
                'CrewChief setup',
                'iRacing setup and optimization',
                'Assetto Corsa CSP / Pure / mod setup',
                'Wheelbase and pedal tuning',
                'Motion platform and bass shaker configuration',
                'OBS and streaming software setup',
                'General sim racing PC optimization',
                'Any other third-party apps',
            ],
        }
    },
}
</script>

<template>
    <div class="scope-page">
        <nav-component />

        <!-- ════════════════════════════════ HEADER ═══════════════════════════════════ -->
        <section class="scope-header">
            <b-container>
                <h1 class="scope-title">Support Team Scope &amp; Billing</h1>
                <p class="scope-intro">
                    Support handles onboarding, troubleshooting, technical support, and sim racing software configuration
                    for ShiftOS, Shift Arcade Franchisees, SimStation customers, and paid sim racing support customers.
                </p>
            </b-container>
        </section>

        <!-- ════════════════════════════════ TABLES ═══════════════════════════════════ -->
        <section class="scope-body">
            <b-container>

                <div v-for="(section, si) in sections" :key="si" class="scope-section">
                    <h2 class="section-heading">{{ section.title }}</h2>
                    <b-table
                        :fields="fields"
                        :items="section.items"
                        :hover="true"
                        table-class="scope-table-el"
                    >
                        <template #cell(covered)="{ item }">
                            <i v-if="item.covered" class="ph-fill ph-check-circle status-yes"></i>
                            <i v-else-if="!item.covered" class="ph-fill ph-x-circle status-no"></i>
                            <span v-else class="status-optional">Optional</span>
                        </template>
                        <template #cell(billable)="{ item }">
                            <i v-if="item.billable" class="ph-fill ph-check-circle status-yes"></i>
                            <i v-else-if="!item.billable" class="ph-fill ph-x-circle status-no"></i>
                            <span v-else class="status-optional">Optional</span>
                        </template>
                    </b-table>
                </div>

                <!-- ════════════════════════════════ SALES ════════════════════════════ -->
                <div class="scope-section">
                    <h2 class="section-heading">Sales Opportunities During Onboarding &amp; Support</h2>
                    <div class="sales-box">
                        <p class="sales-body">
                            Onboarding and troubleshooting sessions are the primary opportunity for Support
                            to identify additional customer needs and sell additional configuration services.
                        </p>
                        <p class="sales-note">
                            Most opportunities happen during AnyDesk onboarding and sim troubleshooting calls.
                        </p>
                        <ul class="sales-list">
                            <li v-for="(item, i) in salesOpportunities" :key="i">{{ item }}</li>
                        </ul>
                    </div>
                </div>

            </b-container>
        </section>

        <footer-component />
    </div>

<!-- # OpenAI Fine-Tuning Training Data (JSONL)

```jsonl
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "What does Support handle during ShiftOS onboarding?"}, {"role": "assistant", "content": "Support handles sales handoff onboarding, AnyDesk remote access setup, Steam installation, Assetto Corsa installation, Content Manager setup, ShiftOS Launcher setup, controller configuration, and initial troubleshooting. This work is non-billable."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Is ShiftOS troubleshooting billable?"}, {"role": "assistant", "content": "No. ShiftOS troubleshooting and support are covered by Support and are non-billable."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure SimHub?"}, {"role": "assistant", "content": "Yes. SimHub installation, dashboard setup, bass shaker tuning, and troubleshooting are billable services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Who handles walk-in customers?"}, {"role": "assistant", "content": "Walk-in customers, retail counter operations, arcade floor management, and active in-store customer assistance are handled by a separate in-store operations team, not Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure SimHub?"}, {"role": "assistant", "content": "Yes. SimHub installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure CrewChief?"}, {"role": "assistant", "content": "Yes. CrewChief installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure iRacing?"}, {"role": "assistant", "content": "Yes. iRacing installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure RaceRoom?"}, {"role": "assistant", "content": "Yes. RaceRoom installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure Le Mans Ultimate?"}, {"role": "assistant", "content": "Yes. Le Mans Ultimate installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure Automobilista 2?"}, {"role": "assistant", "content": "Yes. Automobilista 2 installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure Assetto Corsa CSP?"}, {"role": "assistant", "content": "Yes. Assetto Corsa CSP installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure Pure?"}, {"role": "assistant", "content": "Yes. Pure installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure OBS?"}, {"role": "assistant", "content": "Yes. OBS installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure Discord?"}, {"role": "assistant", "content": "Yes. Discord installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure Moza Pit House?"}, {"role": "assistant", "content": "Yes. Moza Pit House installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure Fanalab?"}, {"role": "assistant", "content": "Yes. Fanalab installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support install and configure Trading Paints?"}, {"role": "assistant", "content": "Yes. Trading Paints installation, setup, troubleshooting, and optimization are billable sim racing services handled by Support."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure Simucube wheelbases?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for Simucube wheelbases are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure Fanatec wheelbases?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for Fanatec wheelbases are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure Heusinkveld pedals?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for Heusinkveld pedals are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure bass shakers?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for bass shakers are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure button boxes?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for button boxes are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure triple monitor mounts?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for triple monitor mounts are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure D-Box actuators?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for D-Box actuators are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure Sigma Integrale systems?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for Sigma Integrale systems are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support configure motion platforms?"}, {"role": "assistant", "content": "Yes. Configuration, tuning, and troubleshooting for motion platforms are billable services unless included under SimStation or Franchise support agreements."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support handle Shift Arcade Franchisees?"}, {"role": "assistant", "content": "Yes. Support handles franchise onboarding, troubleshooting, ShiftOS support, D-Box configuration, and franchise technical support coordination."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support handle SimStation customers?"}, {"role": "assistant", "content": "Yes. Support handles SimStation onboarding, AnyDesk remote support, troubleshooting, sim hardware support, and D-Box configuration."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support handle bug reports?"}, {"role": "assistant", "content": "Yes. Support collects, triages, and escalates bug reports and feature requests."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Does Support manage ticket systems?"}, {"role": "assistant", "content": "Yes. Support manages ticket intake, ticket triaging, follow-up, and customer communication."}]}
{"messages": [{"role": "system", "content": "You are an internal operations assistant for a sim racing company."}, {"role": "user", "content": "Can Support sell additional services during onboarding?"}, {"role": "assistant", "content": "Yes. Support should identify opportunities for sim racing optimization, software setup, hardware tuning, display configuration, and streaming setup during onboarding and troubleshooting sessions."}]}
```

## Recommended Fine-Tune Settings

* Base model: `gpt-4.1-mini`
* Format: JSONL
* Training purpose: Internal operations assistant
* Style: Concise, direct, operational
* Temperature during inference: 0.2 - 0.5

## Suggested Additional Data

To improve performance, add:

* Escalation procedures
* Support SLA examples
* Franchise-specific edge cases
* Billing dispute examples
* Hardware troubleshooting workflows
* Customer communication examples
* Ticket triage classifications
* Internal terminology and abbreviations -->


</template>

<style scoped>
/* ── PAGE ────────────────────────────────────────────────────── */
.scope-page {
    background: #050505;
    min-height: 100vh;
    color: #ffffff;
}

/* ── HEADER ──────────────────────────────────────────────────── */
.scope-header {
    padding: 80px 0 56px;
    border-bottom: 1px solid #1a1a1a;
}

.scope-title {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: clamp(32px, 5vw, 44px);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #ffffff;
    margin: 0 0 16px;
    line-height: 1;
}

.scope-intro {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 20px;
    color: #b8b8b8;
    max-width: 860px;
    line-height: 1.55;
    margin: 0;
}

/* ── BODY ────────────────────────────────────────────────────── */
.scope-body {
    padding: 0 0 96px;
}

.scope-section {
    margin-top: 64px;
}

.section-heading {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 26px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #ffffff;
    padding-left: 14px;
    border-left: 4px solid #e8201a;
    margin-bottom: 18px;
    line-height: 1.2;
}

/* ── TABLE ───────────────────────────────────────────────────── */
:deep(.scope-table-el) {
    width: 100%;
    border-collapse: collapse;
    background: #101010;
    border: 1px solid #232323;
    margin: 0;
}

:deep(.scope-table-el thead th) {
    background: #161616;
    color: #9ca3af;
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 16px;
    border-bottom: 1px solid #232323;
    border-top: none;
    vertical-align: middle;
}

:deep(.scope-table-el tbody td) {
    background: transparent;
    color: #ffffff;
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 18px;
    line-height: 1.5;
    padding: 18px 16px;
    border-top: 1px solid #1f1f1f;
    vertical-align: middle;
}

:deep(.scope-table-el tbody tr:hover td) {
    background: #141414;
}

:deep(.scope-table-el .status-col) {
    width: 110px;
    min-width: 110px;
    max-width: 110px;
    text-align: center;
}

/* ── STATUS ICONS ────────────────────────────────────────────── */
.status-yes {
    font-size: 22px;
    color: #22c55e;
}

.status-no {
    font-size: 22px;
    color: #ef4444;
}

.status-optional {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #9ca3af;
}

/* ── SALES BOX ───────────────────────────────────────────────── */
.sales-box {
    background: #101010;
    border: 1px solid #232323;
    padding: 32px;
}

.sales-body {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 18px;
    color: #c8c8c8;
    line-height: 1.55;
    margin: 0 0 14px;
}

.sales-note {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #22c55e;
    margin: 0 0 4px;
}

.sales-list {
    margin: 20px 0 0;
    padding-left: 24px;
    list-style: disc;
}

.sales-list li {
    font-family: 'Rajdhani', system-ui, sans-serif;
    font-size: 18px;
    color: #ffffff;
    margin-bottom: 10px;
    line-height: 1.4;
}

/* ── RESPONSIVE ─────────────────────────────────────────────── */
@media (max-width: 600px) {
    .scope-header {
        padding: 56px 0 36px;
    }

    .scope-body {
        padding-bottom: 64px;
    }

    .scope-section {
        margin-top: 48px;
    }

    .sales-box {
        padding: 24px 20px;
    }
}
</style>
